import React, { useState, useEffect } from "react";
import Button2 from "../../components/elements/forms/Button2";
import { toast } from "sonner";
import {
  manualTransferService,
  consoleService,
} from "../../services/api/settings";
import {
  X,
  Loader2,
  Building2,
  CheckCircle2,
  Info,
  Copy,
  Check,
} from "lucide-react";
import StatusModal from "../../components/Modal/StatusModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { organizationService } from "../../services/api/organization";

interface BankTransferFormData {
  receipt: File | null;
}

interface FormErrors {
  receipt?: string;
}

const PaymentUpload: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const organizationId = searchParams.get("Id") || "";
  const [tier, setTier] = useState(searchParams.get("tier") || "");
  const [frequency, setFrequency] = useState<"MONTHLY" | "YEARLY">(
    (searchParams.get("frequency") as "MONTHLY" | "YEARLY") || "MONTHLY",
  );
  const [amount, setAmount] = useState<number | undefined>(
    searchParams.get("amount") ? Number(searchParams.get("amount")) : undefined,
  );
  const [isConsolePlan, setIsConsolePlan] = useState(
    searchParams.get("isConsole") === "true",
  );
  const [planId, setPlanId] = useState(searchParams.get("planId") || "");

  // Feature IDs and other arrays
  const [featureIds, setFeatureIds] = useState<string[]>(
    searchParams.get("featureIds")?.split(",") || [],
  );
  const [directAddOnIds, setDirectAddOnIds] = useState<string[]>(
    searchParams.get("directAddOnIds")?.split(",") || [],
  );
  const [deploymentIds, setDeploymentIds] = useState<string[]>(
    searchParams.get("deploymentIds")?.split(",") || [],
  );
  const [integrationIds, setIntegrationIds] = useState<string[]>(
    searchParams.get("integrationIds")?.split(",") || [],
  );

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [amountInNaira, setAmountInNaira] = useState<number | null>(null);
  const [reference, setReference] = useState<string>(
    searchParams.get("ref") ||
      `25TH-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
  );
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState<BankTransferFormData>({
    receipt: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });

  // Fetch organization data if Id is present
  useEffect(() => {
    if (organizationId) {
      fetchOrganizationDetails();
    }
  }, [organizationId]);

  const fetchOrganizationDetails = async () => {
    try {
      setFetching(true);
      const response = await organizationService.get(organizationId);
      if (response && response.data) {
        const org = response.data;
        // If some params are missing, use the latest preselected package
        const latestPackage = org.preselectedPackages?.[0];

        if (latestPackage) {
          if (!tier) setTier(latestPackage.tier);
          if (!searchParams.get("frequency"))
            setFrequency(latestPackage.frequency as "MONTHLY" | "YEARLY");
          if (amount === undefined) setAmount(latestPackage.totalAmount);
          if (featureIds.length === 0)
            setFeatureIds(latestPackage.features?.map((f: any) => f.id) || []);
          if (directAddOnIds.length === 0)
            setDirectAddOnIds(
              latestPackage.directFeatures?.map((f: any) => f.id) || [],
            );
        }
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
      toast.error("Could not retrieve organization details");
    } finally {
      setFetching(false);
    }
  };

  // Prepare manual transfer once we have enough data
  // useEffect(() => {
  //   if (organizationId && tier && !isConsolePlan && !fetching) {
  //     prepareManualTransfer();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [organizationId, tier, fetching]);

  // const prepareManualTransfer = async () => {
  //   try {
  //     setPreparing(true);
  //     let response;
  //     if (isConsolePlan) {
  //       response = await consoleService.prepareManualTransfer({
  //         organizationId,
  //         planId: planId || tier,
  //         frequency,
  //         integrationIds,
  //         deploymentCostIds: deploymentIds,
  //       });
  //     } else {
  //       console.log("Preparing manual transfer for tier:", tier);
  //       console.log("Preparing manual transfer for featureIds:", featureIds);
  //       console.log(
  //         "Preparing manual transfer for directFeatureIds:",
  //         directAddOnIds,
  //       );
  //       console.log("Preparing manual transfer for amount:", amount);
  //       console.log(
  //         "Preparing manual transfer for deploymentIds:",
  //         deploymentIds,
  //       );
  //       console.log(
  //         "Preparing manual transfer for integrationIds:",
  //         integrationIds,
  //       );
  //       response = await manualTransferService.prepare({
  //         tier,
  //         organizationId,
  //         frequency,
  //         featureIds: featureIds.length > 0 ? featureIds : undefined,
  //         directPlan: tier.startsWith("DIRECT_")
  //           ? {
  //               tier: tier as any,
  //               includeSetupFee: searchParams.get("includeSetupFee") === "true",
  //             }
  //           : undefined,
  //         directFeatureIds:
  //           directAddOnIds.length > 0 ? directAddOnIds : undefined,
  //         amount,
  //       });
  //     }

  //     if (response.status === "success" && response.data) {
  //       setAmountInNaira(response.data.amountInNaira);
  //       toast.success(
  //         response.message || "Payment details prepared successfully",
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Error preparing manual transfer:", error);
  //     toast.error("Failed to prepare payment details. Please try again.");
  //   } finally {
  //     setPreparing(false);
  //   }
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    // Validate file type and size
    if (file) {
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          receipt: "Please upload a valid file (JPG, PNG, or PDF)",
        }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          receipt: "File size must be less than 5MB",
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      receipt: file,
    }));

    if (errors.receipt) {
      setErrors((prev) => ({
        ...prev,
        receipt: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.receipt) {
      newErrors.receipt = "Payment receipt is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    if (!formData.receipt) {
      toast.error("Please upload a payment receipt");
      return;
    }

    setLoading(true);

    try {
      let response;
      if (isConsolePlan) {
        response = await consoleService.submitReceipt(
          formData.receipt,
          organizationId,
          planId || tier,
          frequency,
          reference,
        );
      } else {
        response = await manualTransferService.submitReceipt(
          formData.receipt,
          organizationId,
          tier,
          frequency,
          featureIds,
          tier.startsWith("DIRECT_")
            ? {
                tier: tier as any,
                includeSetupFee: searchParams.get("includeSetupFee") === "true",
              }
            : undefined,
          directAddOnIds,
          amount,
          reference,
        );
      }

      if (response.status === "success") {
        // Reset form
        setFormData({
          receipt: null,
        });
        setErrors({});
        // onClose(); // We'll let the StatusModal action handle the close or keep it above
        setStatusModal({
          isOpen: true,
          status: "success",
          message: response.message || "Payment receipt submitted!",
        });
      }
    } catch (error: any) {
      console.error("Payment receipt submission error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit payment receipt";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 flex items-center justify-center font-abel">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-[600px] overflow-hidden border border-gray-100">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b">
            <h2 className="text-[28px] font-bold text-[#212121]">
              Payment Verification
            </h2>
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
              <Building2 className="w-6 h-6" />
            </div>
          </div>

          {/* Bank Account Information */}
          <div className="mb-8 p-6 bg-[#F9FAFB] rounded-xl border border-gray-100">
            <h3 className="font-bold text-[18px] mb-4 text-[#212121] flex items-center gap-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full" />
              Transfer Information
            </h3>
            <div className="space-y-1 text-[15px] text-gray-700">
              <div className="flex justify-between items-center pb-1 border-b border-gray-50">
                <span className="text-gray-500">Bank Name</span>
                <span className="font-bold">First Bank of Nigeria</span>
              </div>
              <div className="flex justify-between items-center pb-1 border-b border-gray-50">
                <span className="text-gray-500">Account Name</span>
                <span className="font-bold">25th & Staffing Limited</span>
              </div>
              <div className="flex justify-between items-center pb-1 border-b border-gray-50">
                <span className="text-gray-500">Account Number</span>
                <span className="font-bold text-lg">2028414197</span>
              </div>

              {preparing ? (
                <div className="flex items-center gap-2 mt-4 text-blue-600 animate-pulse">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Preparing secure payment gateway...</span>
                </div>
              ) : amountInNaira !== null || amount ? (
                <div className="mt-4 p-4 bg-blue-600 rounded-lg text-white">
                  <p className="text-sm opacity-80 mb-1">
                    Total Amount Payable
                  </p>
                  <p className="font-bold text-2xl">
                    ₦{(amountInNaira || amount || 0).toLocaleString()}
                  </p>
                </div>
              ) : null}

              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-gray-500 mb-1 text-sm font-medium">
                  Payment Reference
                </p>
                <div className="flex items-center justify-between bg-blue-50 px-4 py-1 rounded-xl border border-blue-100">
                  <span className="font-mono font-bold text-blue-700 text-sm tracking-wider">
                    {reference}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(reference);
                      setCopied(true);
                      toast.success("Reference copied!");
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-500 shadow-sm hover:shadow-md transition-all active:scale-95"
                  >
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          {fetching || preparing ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-500 font-medium tracking-wide">
                {fetching
                  ? "Retrieving organization details..."
                  : "Initializing payment gateway..."}
              </p>
              {!fetching && preparing && (
                <div className="mt-4 flex flex-col items-center gap-2 max-w-[300px] text-center">
                  <div className="flex items-center gap-2 text-blue-600 font-semibold mb-2">
                    <Info className="w-4 h-4" />
                    <span className="text-sm">Quick Note</span>
                  </div>
                  <p className="text-xs text-gray-400">
                    We're setting up a secure transfer route for your{" "}
                    <span className="font-bold text-gray-600">
                      {tier.replace(/_/g, " ")}
                    </span>{" "}
                    plan.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* File Upload */}
              <div className="space-y-2">
                <label className="font-bold block text-[#1E1E1E] text-[16px]">
                  Upload Proof of Payment{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative group">
                  <input
                    type="file"
                    name="receipt"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div
                    className={`p-8 border-2 border-dashed rounded-xl transition-all flex flex-col items-center justify-center gap-3 ${
                      formData.receipt
                        ? "border-green-400 bg-green-50"
                        : errors.receipt
                          ? "border-red-300 bg-red-50"
                          : "border-gray-200 group-hover:border-blue-400 group-hover:bg-blue-50"
                    }`}
                  >
                    {formData.receipt ? (
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-500">
                        <Loader2 className="w-6 h-6" />
                      </div>
                    )}
                    <span className="font-medium text-gray-600">
                      {formData.receipt
                        ? formData.receipt.name
                        : "Click or drag receipt here"}
                    </span>
                    <span className="text-xs text-gray-400">
                      JPG, PNG, or PDF (Max 5MB)
                    </span>
                  </div>
                </div>
                {errors.receipt && (
                  <p className="text-red-500 text-sm font-medium">
                    {errors.receipt}
                  </p>
                )}
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button2
                  title="Go Back"
                  onSubmitHandler={handleCancel}
                  className="flex-1 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 h-[55px]"
                  disabled={loading || preparing}
                />
                <button
                  type="submit"
                  disabled={loading || preparing}
                  // disabled={loading || preparing || (!amountInNaira && !amount)}
                  className={`flex-[2] bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 border-none flex items-center justify-center text-[16px] font-bold py-3 px-6 rounded-xl h-[55px] transition-all ${
                    loading || preparing
                      ? "opacity-50 cursor-not-allowed grayscale"
                      : "hover:-translate-y-0.5"
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifying Receipt...
                    </span>
                  ) : (
                    "Submit Payment Verification"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () => {
                navigate("/"); // Or to a success dashboard
              }
            : undefined
        }
      />
    </div>
  );
};

export default PaymentUpload;
